<div
  class="relative w-full aspect-video bg-neutral rounded-lg overflow-hidden"
  role="region"
  aria-label="Video player"
  data-testid="bunny-player-section"
  [ngClass]="{
    'cursor-pointer': isThumbnailView,
    'cursor-none': controlsService.isPlaying && !controlsService.controlsVisible && !isThumbnailView
  }"
  (click)="onVideoClick()"
  (mousemove)="controlsService.onVideoContainerMouseMove()"
  (mouseleave)="controlsService.onVideoContainerMouseLeave()"
  (touchstart)="controlsService.showControls()"
  >
  <!-- Debug Video Info Overlay -->
  @if (debug.isEnabled('videoInfo') && (debugVideoName || videoId)) {
    <div class="absolute top-2 right-2 z-50 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded max-w-[200px] pointer-events-none" data-testid="bunny-player-debug-overlay">
      @if (debugVideoName) {
        <div class="truncate">{{ debugVideoName }}</div>
      }
      @if (videoId) {
        <div class="text-white/70">VID: {{ videoId | slice:0:8 }}...</div>
      }
    </div>
  }
  @if (isBunnyUrl) {
    @if (!isProcessing || isThumbnailView) {
      <iframe
        #iframePlayer
        [src]="safeEmbedUrl"
        class="absolute inset-0 w-full h-full rounded-lg"
        title="Video Player"
        frameborder="0"
        allowfullscreen
        >
      </iframe>
      @if (isThumbnailView) {
        <div class="absolute inset-0 flex items-center justify-center bg-base-content/20">
          @if (isProcessing) {
            <div class="badge badge-neutral absolute top-2 right-2 gap-1">
              <app-icon name="settings" size="sm" class="animate-spin"></app-icon>
              <span class="text-xs">Processing</span>
            </div>
          }
          @if (!isProcessing) {
            <div class="btn btn-circle btn-lg bg-base-content/40 hover:bg-base-content/60 border-none text-base-100 hover:scale-110 transition-all">
              <app-icon name="play_arrow" size="md"></app-icon>
            </div>
          }
        </div>
      }
    } @else {
      <div class="absolute inset-0 flex items-center justify-center bg-base-300" data-testid="bunny-player-processing">
        <div class="text-center text-base-content p-5 rounded-lg max-w-xs">
          <div class="loading loading-spinner loading-lg mb-4 text-base-content"></div>
          <h3 class="text-lg font-semibold mb-2">Video Processing</h3>
          <p class="text-sm text-base-content opacity-80 mb-4">
            {{
            videoStatus?.statusMessage ||
            'Your video is being processed. This may take a few minutes.'
            }}
          </p>
          @if (videoStatus?.encodeProgress) {
            <div class="w-full bg-base-300 rounded-full h-2 mb-2">
              <div
                class="bg-primary h-2 rounded-full transition-all duration-500"
                [style.width.%]="videoStatus?.encodeProgress"
              ></div>
            </div>
          }
          @if (videoStatus?.encodeProgress) {
            <p class="text-xs text-base-content opacity-70">
              {{ videoStatus?.encodeProgress }}% complete
            </p>
          }
        </div>
      </div>
    }
  } @else {
    <!-- Loading State -->
    @if (isLoadingVideo) {
      <div class="absolute inset-0 flex items-center justify-center bg-base-300" data-testid="bunny-player-loading">
        <div class="text-center text-base-content p-5 rounded-lg max-w-xs">
          <div class="loading loading-spinner loading-lg mb-4 text-base-content"></div>
          <h3 class="text-lg font-semibold mb-2">Loading Video</h3>
          <p class="text-sm text-base-content opacity-80">
            Preparing video for playback...
          </p>
        </div>
      </div>
    }
    <!-- Video Player -->
    @if (processedVideoUrl !== '' && !isLoadingVideo) {
      <video
        #videoPlayer
        [attr.src]="!isHlsStream && processedVideoUrl ? processedVideoUrl : null"
        [controls]="false"
        (ended)="onVideoEnded()"
        (error)="onVideoElementError()"
        (timeupdate)="onTimeUpdate($event)"
        (loadedmetadata)="onVideoLoaded()"
        (seeking)="onSeeking($event)"
        (seeked)="onSeeked($event)"
        (click)="controlsService.togglePlayPause()"
        class="absolute inset-0 w-full h-full object-contain rounded-lg cursor-pointer"
        [attr.preload]="isThumbnailView ? 'none' : 'auto'"
        [muted]="isThumbnailView"
        playsinline
        crossorigin="anonymous"
      ></video>
    }
    <!-- Play Overlay - Shows until user clicks play -->
    @if (!isThumbnailView && controlsService.showPlayOverlay && processedVideoUrl !== '') {
      <div
        class="absolute inset-0 flex items-center justify-center bg-base-content/20 cursor-pointer"
        (click)="controlsService.startVideoPlayback()">
        <div class="btn btn-circle btn-lg bg-base-content/40 hover:bg-base-content/60 border-none text-base-100 hover:scale-110 transition-all">
          <app-icon name="play_arrow" size="md"></app-icon>
        </div>
      </div>
    }
    <!-- KAN-576: Playback Failed State. Distinct from "no URL could be built"
         below: here a URL WAS built and the stream would not load (typically a
         404 on playlist.m3u8). Previously this rendered nothing at all and the
         viewer was left with a blank grey box at 0:00 / 0:00. -->
    @if (playbackFailed) {
      <div
        class="absolute inset-0 flex items-center justify-center bg-base-300"
        data-testid="bunny-player-playback-failed">
        <div class="text-center p-5 rounded-lg max-w-xs">
          <app-icon name="videocam_off" size="xl" class="text-base-content opacity-50 mb-4"></app-icon>
          <h3 class="text-lg font-semibold mb-2">Video Unavailable</h3>
          <p class="text-sm text-base-content opacity-80">
            This video could not be loaded. It may still be processing, or its
            source may have moved. Please try again later.
          </p>
        </div>
      </div>
    }
    <!-- No Video Available State -->
    @if (processedVideoUrl === '' && !isLoadingVideo && (videoUrl !== '' || videoId !== '')) {
      <div
        class="absolute inset-0 flex items-center justify-center bg-base-300"
        data-testid="bunny-player-unavailable">
        <div class="text-center p-5 rounded-lg max-w-xs">
          <app-icon name="videocam_off" size="xl" class="text-base-content opacity-50 mb-4"></app-icon>
          <h3 class="text-lg font-semibold mb-2">Video Unavailable</h3>
          <p class="text-sm text-base-content opacity-80">
            Unable to load video content. Please try again later.
          </p>
        </div>
      </div>
    }
    <!-- Custom Video Controls (auto-hide) -->
    @if (!isThumbnailView && processedVideoUrl !== '') {
      <div
        class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-base-300/90 to-transparent px-4 pb-4 pt-8 transition-opacity duration-300"
        [ngClass]="{'opacity-100': controlsService.controlsVisible || !controlsService.isPlaying, 'opacity-0 pointer-events-none': !controlsService.controlsVisible && controlsService.isPlaying}"
        (mouseenter)="controlsService.showControls()"
        (click)="$event.stopPropagation()">
        <!-- Progress Bar -->
        <div class="mb-3">
          <div class="relative h-1 bg-base-content/30 rounded-full cursor-pointer group"
            (click)="seekToPosition($event)"
            #progressBar>
            <!-- Buffered progress -->
            <div class="absolute inset-0 h-full bg-base-content/20 rounded-full"></div>
            <!-- Progress (max-watched, backend-verified -- KAN-306) -->
            <div class="absolute left-0 h-full bg-success rounded-full transition-all duration-100"
            [style.width.%]="controlsService.watchedPercentage"></div>
            <!-- Current playback position -->
            <div class="absolute left-0 h-full bg-primary rounded-full transition-all duration-100"
              [style.width.%]="controlsService.currentPercentage">
              <!-- Scrubber handle -->
              <div class="absolute -right-2 -top-1 w-3 h-3 bg-base-100 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          </div>
        </div>
        <!-- Control Buttons -->
        <div
          class="flex flex-wrap items-center justify-between gap-y-2"
          data-testid="bunny-player-control-row">
          <div class="flex items-center gap-2">
            <!-- Play/Pause Button -->
            <button (click)="controlsService.togglePlayPause()"
              class="btn btn-circle btn-sm bg-base-content/10 border-none hover:bg-base-content/20 text-base-content"
              [attr.aria-label]="controlsService.isPlaying ? 'Pause' : 'Play'"
              data-testid="bunny-player-play-pause-btn">
              <app-icon [name]="controlsService.isPlaying ? 'pause' : 'play_arrow'" size="sm"></app-icon>
            </button>
            <!-- Time Display + the shared watch-progress chip (KAN-306). The mm:ss
                 readout stays primary; the chip is the app's ONE way of stating
                 watch progress and is the same component the status bar and quiz
                 panel render. controlsService.watchedPercentage is pushed the
                 badge's exact value each tick via
                 split-screen-player.syncWatchedPercentage() -- same max-watched
                 metric, same tick, same primitive -- so the two numbers cannot
                 diverge or lag. -->
            <span class="text-base-content text-sm ml-2 inline-flex items-center gap-2">
              {{ formatTime(controlsService.currentTime) }} / {{ formatTime(controlsService.displayDuration) }}
              @if (showWatchProgress) {
                <app-watch-progress-chip
                  size="xs"
                  [onVideo]="true"
                  [percent]="controlsService.watchedPercentage"
                  [completed]="watchCompleted"
                  [live]="controlsService.isPlaying"></app-watch-progress-chip>
              }
            </span>
          </div>
          <div
            class="flex items-center gap-2 ml-auto"
            data-testid="bunny-player-trailing-controls">
            <!-- Volume Control -->
            <div class="flex items-center gap-1">
              <button (click)="controlsService.toggleMute()"
                class="btn btn-circle btn-xs bg-transparent border-none text-base-content hover:bg-base-content/20"
                [attr.aria-label]="controlsService.isMuted ? 'Unmute' : 'Mute'"
                data-testid="bunny-player-mute-btn">
                <app-icon [name]="controlsService.isMuted ? 'volume_off' : 'volume_up'" size="xs"></app-icon>
              </button>
              <input type="range"
                [(ngModel)]="controlsService.volume"
                (input)="controlsService.onVolumeChange($event)"
                min="0" max="100"
                aria-label="Volume"
                class="range range-xs w-20 [--range-shdw:transparent]">
            </div>
            <!-- Speed Control -->
            <div class="relative">
              <button (click)="speedService.toggleSpeedMenu()"
                class="btn btn-xs bg-base-content/10 border-none text-base-content hover:bg-base-content/20"
                aria-label="Playback speed"
                [attr.aria-expanded]="speedService.showSpeedMenu"
                data-testid="bunny-player-speed-btn">
                {{ speedService.formatSpeedLabel(speedService.playbackSpeed) }}
              </button>
              <!-- Speed Menu -->
              @if (speedService.showSpeedMenu) {
                <div
                  class="speed-menu absolute bottom-full right-0 mb-2 bg-base-100 rounded-lg shadow-lg p-2 min-w-max z-50">
                  <div class="text-xs opacity-75 mb-2">Playback Speed</div>
                  <div class="flex flex-col gap-1">
                    @for (speed of speedService.availableSpeeds; track speed) {
                      <button
                        (click)="speedService.setPlaybackSpeed(speed)"
                        class="btn btn-xs border-none hover:bg-base-200"
                        [class.bg-primary]="speed === speedService.playbackSpeed"
                        [class.text-primary-content]="speed === speedService.playbackSpeed">
                        {{ speedService.formatSpeedLabel(speed) }}
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
            @if (isHlsStream) {
              <app-video-quality-selector
                [levels]="qualityLevels"
                [selectedLevel]="selectedQualityLevel"
                [activeLevel]="activeQualityLevel"
                [manualSelectionAvailable]="manualQualitySelectionAvailable"
                (qualitySelected)="onQualitySelected($event)">
              </app-video-quality-selector>
            }
            <!-- Fullscreen Button -->
            <button (click)="controlsService.toggleFullscreen()"
              class="btn btn-circle btn-xs bg-transparent border-none text-base-content hover:bg-base-content/20"
              [attr.aria-label]="controlsService.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
              data-testid="bunny-player-fullscreen-btn">
              <app-icon [name]="controlsService.isFullscreen ? 'fullscreen_exit' : 'fullscreen'" size="xs"></app-icon>
            </button>
          </div>
        </div>
      </div>
    }
    @if (isThumbnailView) {
      <div class="absolute inset-0 flex items-center justify-center bg-base-content/20">
        <div class="btn btn-circle btn-lg bg-base-content/40 hover:bg-base-content/60 border-none text-base-100 hover:scale-110 transition-all">
          <app-icon name="play_arrow" size="md"></app-icon>
        </div>
      </div>
    }
  }

</div>
